import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import {
	Typography,
	Theme,
	RadioGroup,
	Radio,
	Box,
	FormGroup,
	FormControlLabel,
} from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import {
	Shopper,
	ProductApi,
	OrderObject,
	OrderProduct,
	ShopPublicInfo,
	Languages,
	PurchaseTypes,
} from 'common/types';
import {
	createOrderProductFromProductApi,
	newFirestoreId,
	PartialOrderProductFields,
} from 'common/utils/newRentalUtils';
import { createUpdatedRentalOrderObject } from '../../utils';
import { updateActiveRentalToDb } from 'services/utils';
import { notUndefined } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import {
	getEarliestProductStartTime,
	getLatestProductEndTime,
	getTimeDifferenceInSeconds,
} from 'common/utils/dateCalculations';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { useTranslation } from 'services/localization';
import {
	getAllProductVariantIds,
	getProductVariants,
	getSetProducts,
	getVariantsBySkuId,
} from 'common/modules/products/variants';
import BackdropDialog from 'common/components/BackdropDialog';
import { usePermissions } from 'hooks/usePermissions';
import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import useAvailability from 'common/hooks/useAvailability';
import OverbookedBanner from 'components/OverbookedBanner';
import { getMinAvailabilityFromRange } from 'common/api/frontend/inventory_new/availabilityRange';
import { InventoryItem } from 'common/modules/inventory';
import { getProductsBySkuId } from 'common/modules/products/utils';
import { uniq } from 'lodash';
import { getDefaultTimezone } from 'common/utils/dateUtils';

interface OwnProps {
	open: boolean;
	scannedItem: InventoryItem;
	stockProducts: ProductApi[];
	shopper: Shopper;
	shoppers: Shopper[];
	activeRental: OrderObject;
	shopPublicInfo: ShopPublicInfo;
	scannedCode: string;
	lang: Languages;
	handleClose: () => void;
	useDinVariation: boolean;
}

type Props = OwnProps & WithStyles<typeof styles>;

const AddScannedProductDialog = (props: Props) => {
	const {
		classes,
		scannedItem,
		shopper,
		stockProducts,
		activeRental,
		shopPublicInfo,
		shoppers,
		scannedCode,
		lang,
		useDinVariation,
	} = props;
	const [updatingOrder, setUpdatingOrder] = useState(false);
	const { hasPermission } = usePermissions();
	const allowOverbookings = hasPermission('allow_overbookings');
	const locationId = useSelector(UserSelectors.userActiveLocationId);
	const activeShopAllLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const shopTimezone = useSelector(ShopSelectors.getShopTimeZone) ?? getDefaultTimezone();
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);

	const { t } = useTranslation();
	const currency = getCurrencyObjectFromCurrencyCode(
		activeRental.rentalInfo.pricing.currency,
		shopPublicInfo.currency,
	);

	const getNewOrderProduct = (productId: string) => {
		const stockProduct = stockProducts.find((stockP) => stockP.id === productId);
		if (!stockProduct) return null;
		const getPartialOrderProductFields = () => {
			const getDurationTypeFromShopperProducts = (shopperProducts: OrderProduct[]) => {
				return shopperProducts[0].durationType;
			};
			const rentalId = activeRental.rentalInfo.id;
			const shopperProducts = activeRental.products.filter((p) => p.shopperId === shopper.id);
			const startDate =
				activeRental.rentalInfo.rentalState === 'ACTIVE'
					? moment().toISOString()
					: getEarliestProductStartTime(shopperProducts) || '';
			const endDate: string = getLatestProductEndTime(shopperProducts) || '';
			const rentalDurationInSeconds = getTimeDifferenceInSeconds(startDate, endDate) || 0;
			const durationType = getDurationTypeFromShopperProducts(shopperProducts);
			const orderProductId = newFirestoreId();

			const partialOrderProductFields: PartialOrderProductFields = {
				id: orderProductId,
				startDate,
				endDate,
				rentalDurationInSeconds,
				shopperId: shopper.id,
				rentalId,
				selectedAsAdditional: false,
				durationType,
				durationName: null,
				startLocationId: activeRental.rentalInfo.startLocation.id,
				endLocationId: activeRental.rentalInfo.endLocation.id,
			};

			return partialOrderProductFields;
		};

		const getScannedVariantFromSkuId = (
			product: ProductApi,
			stockProducts: ProductApi[],
			skuId: string,
		) => {
			const validProductVariants = [
				product,
				...getSetProducts(product, stockProducts),
			].flatMap((p) => getProductVariants(p));
			// Naive implementation - in case of multiple variants having same sku, it returns the first found
			const variant = getVariantsBySkuId(validProductVariants, skuId)?.[0];
			return variant;
		};

		const partialOrderProductFields = getPartialOrderProductFields();
		const { rentalInfo } = activeRental;
		const taxExcluded = rentalInfo.pricing.taxExcluded;
		const savedPricingTables = shopPublicInfo.pricingTables;
		const scannedSkuId = scannedItem.skuId;
		const scannedVariant = !scannedSkuId
			? undefined
			: getScannedVariantFromSkuId(stockProduct, stockProducts, scannedSkuId);
		const variantId = scannedVariant?.id;
		const mainProductVariant =
			stockProduct.variants.options.find((v) => v.id === variantId) ??
			stockProduct.variants.options[0];
		const selectedVariantIds = uniq([variantId, mainProductVariant.id].filter(notUndefined));
		const productVariantIds = getAllProductVariantIds(
			stockProduct,
			stockProducts,
			selectedVariantIds,
		);
		const newOrderProduct = createOrderProductFromProductApi({
			stockProduct,
			stockProductListForSetProducts: stockProducts,
			partialOrderProductFields,
			savedPricingTables: savedPricingTables ?? {},
			currencyCode: currency.code,
			taxExcluded,
			segment: null,
			selectedVariant: mainProductVariant,
			productVariantIds,
			channel: 'ADMIN',
			purchaseType: PurchaseTypes.rental,
			timezone: shopTimezone,
			openingHours,
		});
		return newOrderProduct;
	};

	const possibleStockProducts = !scannedItem.skuId
		? []
		: getProductsBySkuId(stockProducts, scannedItem.skuId);
	const possibleStockProductIds = possibleStockProducts.map((p) => p.id);
	const firstProductMatchId: string | undefined = possibleStockProducts[0]?.id;

	const [newOrderProduct, setNewOrderProduct] = useState<OrderProduct | null>(
		!firstProductMatchId ? null : getNewOrderProduct(firstProductMatchId),
	);

	const selectedProductAsProductApi = stockProducts.find(
		(p) => p.id === newOrderProduct?.productApiId,
	);

	const { availability } = useAvailability(selectedProductAsProductApi?.id ?? '', {
		stockProducts,
		startDate: activeRental.rentalInfo.startDate,
		endDate: getLatestProductEndTime(activeRental.products) || '',
		shopId: shopPublicInfo.shopId,
		locationId: locationId ?? '',
		shopLocations: activeShopAllLocations,
		purchaseType: 'rental',
	});

	const isLoading = availability.loading;

	const unavailableVariantIds =
		!!availability.loading || !newOrderProduct
			? []
			: newOrderProduct.summary.variantIds.filter((variantId) => {
					if (!availability.data || !availability.data[variantId]) return false;
					const minAvailability = getMinAvailabilityFromRange(availability.data[variantId]);
					if (minAvailability === null || minAvailability > 0) {
						return false;
					}
					return true;
			  });

	const isValidSelection = !unavailableVariantIds.length;

	const disableAddProductsButton = !allowOverbookings && !isValidSelection;

	const showBanner = !isLoading && !isValidSelection;

	const getNewOrderProductWithCode = (newOrderProduct: OrderProduct) => {
		if (!newOrderProduct.set) {
			return {
				...newOrderProduct,
				productCode: scannedCode,
			};
		}

		// if newOrderProduct is set, update productCode for right set child.
		let bootSoleLengthToUpdate: string | null = null;
		let productCodeAssigned = false;
		let updatedSetProducts = newOrderProduct.setProducts.map((setP) => {
			if (possibleStockProductIds.includes(setP.productApiId) && !productCodeAssigned) {
				let updatedSetP = {
					...setP,
					productCode: scannedCode,
				};

				if (updatedSetP.collectDin) {
					const dinVariation = useDinVariation
						? scannedItem.fixedFieldValues?.dinVariation ?? null
						: null;
					updatedSetP = {
						...updatedSetP,
						dinValues: {
							...updatedSetP.dinValues,
							dinVariation,
						},
					};
				}
				if (setP.collectBootSoleLength) {
					bootSoleLengthToUpdate = scannedItem?.fixedFieldValues?.soleLength ?? null;
				}
				productCodeAssigned = true;
				return updatedSetP;
			}
			return setP;
		});

		if (bootSoleLengthToUpdate) {
			updatedSetProducts = updatedSetProducts.map((setP) => {
				if (setP.collectDin) {
					return {
						...setP,
						dinValues: {
							...setP.dinValues,
							...(bootSoleLengthToUpdate && { soleLength: bootSoleLengthToUpdate }),
						},
					};
				}
				return setP;
			});
		}

		return {
			...newOrderProduct,
			setProducts: updatedSetProducts,
		};
	};

	const updateRentalOrder = async () => {
		if (!newOrderProduct) return;
		setUpdatingOrder(true);
		const { rentalInfo, orderDelivery } = activeRental;
		let newOrderProductWithCode = getNewOrderProductWithCode(newOrderProduct);
		if (newOrderProductWithCode.collectDin) {
			const dinVariation = useDinVariation
				? scannedItem?.fixedFieldValues?.dinVariation ?? null
				: null;
			newOrderProductWithCode = {
				...newOrderProductWithCode,
				dinValues: {
					...newOrderProductWithCode.dinValues,
					dinVariation,
				},
			};
		}
		let bootSoleLengthToUpdate: string | null = null;
		let updatedProducts = activeRental.products;
		if (newOrderProductWithCode.collectBootSoleLength) {
			bootSoleLengthToUpdate = scannedItem.fixedFieldValues?.soleLength || null;
			updatedProducts = getUpdatedProductsWithSoleLength(updatedProducts, bootSoleLengthToUpdate);
		}
		const updatedRentalOrderObject = createUpdatedRentalOrderObject(
			rentalInfo,
			updatedProducts,
			[newOrderProductWithCode],
			shoppers,
			orderDelivery,
		);
		try {
			await updateActiveRentalToDb(updatedRentalOrderObject);
			setUpdatingOrder(false);
			props.handleClose();
		} catch (e) {
			setUpdatingOrder(false);
			// TODO - feedback if did not work.
		}
	};

	const getUpdatedProductsWithSoleLength = (
		allProducts: OrderProduct[],
		bootSoleLengthToUpdate: string | null,
	) => {
		const updatedProducts = allProducts.map((product) => {
			if (product.shopperId === shopper.id) {
				if (product.set && product.setProducts) {
					const setProducts = product.setProducts.map((setProduct) => {
						if (setProduct.collectDin) {
							return {
								...setProduct,
								dinValues: {
									...setProduct.dinValues,
									...(bootSoleLengthToUpdate && { soleLength: bootSoleLengthToUpdate }),
								},
							};
						}
						return setProduct;
					});
					if (product.collectDin) {
						return {
							...product,
							setProducts: setProducts || product.setProducts,
							dinValues: {
								...product.dinValues,
								...(bootSoleLengthToUpdate && { soleLength: bootSoleLengthToUpdate }),
							},
						};
					}
					return {
						...product,
						setProducts,
					};
				}
				if (product.collectDin) {
					return {
						...product,
						dinValues: {
							...product.dinValues,
							...(bootSoleLengthToUpdate && { soleLength: bootSoleLengthToUpdate }),
						},
					};
				}
				return product;
			}
			return product;
		});
		return updatedProducts;
	};

	const getSetsScannedProductsBelongsTo = () => {
		const setsScannedProductBelongsTo = stockProducts.filter((stockP) => {
			if (
				stockP.setProductIds &&
				stockP.setProductIds.some((pId) => possibleStockProductIds.includes(pId))
			) {
				return true;
			}
			return false;
		});
		return setsScannedProductBelongsTo;
	};

	const handleProductChange = (productId: string) => {
		setNewOrderProduct(getNewOrderProduct(productId));
	};

	const setsScannedProductBelongsTo = getSetsScannedProductsBelongsTo();
	const allProductOptions = [...possibleStockProducts, ...setsScannedProductBelongsTo];
	const renderProductOptions = () => {
		return (
			<div className={classes.setOptions}>
				<Typography variant="body1">
					{t('barcode.whichProduct', {
						shopperName: shopper.firstName,
						defaultValue: 'What product do you want to add for {{shopperName}}',
					}) + '?'}
				</Typography>
				<FormGroup>
					<RadioGroup
						name="scannedProduct"
						value={newOrderProduct?.productApiId}
						onChange={(e) => handleProductChange(e.target.value)}
						color="primary"
					>
						{allProductOptions.map((product) => (
							<FormControlLabel
								value={product.id}
								control={<Radio color="primary" />}
								label={getTranslation(product.name, lang)}
							/>
						))}
					</RadioGroup>
				</FormGroup>
			</div>
		);
	};

	return (
		<BackdropDialog
			isOpen={props.open}
			onClose={props.handleClose}
			label={t('barcode.addProduct', 'Add product')}
			body={
				<>
					{!!allProductOptions.length ? (
						renderProductOptions()
					) : (
						<Typography variant="body1" className={classes.dialogHeader} data-qa="dialogTitle">
							{t('barcode.addProductBody', {
								scannedProduct: selectedProductAsProductApi
									? getTranslation(selectedProductAsProductApi.name, lang)
									: '',
								firstName: shopper.firstName,
								defaultValue: 'Add {{scannedProduct}} for {{firstName}}',
							}) + '?'}
						</Typography>
					)}
					{showBanner && !!newOrderProduct && (
						<Box mt={-3}>
							<OverbookedBanner
								allowOverbookings={allowOverbookings}
								rentalProducts={[newOrderProduct]}
								overbookedVariantIds={unavailableVariantIds}
								styling={{ isBarcodeScanner: true }}
							/>
						</Box>
					)}
				</>
			}
			width="xs"
			confirm={{
				label: t('common:actions.add'),
				callback: () => updateRentalOrder(),
				loading: isLoading,
				closeAfterDone: true,
				disabled:
					updatingOrder ||
					(!allowOverbookings && disableAddProductsButton) ||
					isLoading ||
					!newOrderProduct,
			}}
			cancel={{
				label: t('common:actions.cancel'),
				autoFocus: false,
			}}
		/>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		dialogRoot: {
			maxWidth: 800,
		},
		content: {
			width: '100%',
			margin: '0 auto',
			maxWidth: 800,
			padding: theme.spacing(5),
		},
		dialogHeader: {
			marginBottom: theme.spacing(1.5),
		},
		setOptions: {
			marginBottom: theme.spacing(4),
		},
		buttonContainer: {
			textAlign: 'right',
			'& > *': {
				margin: 5,
			},
			paddingRight: `calc(30px - ${theme.spacing(1)}) !important`,
		},
	});

export default withStyles(styles)(AddScannedProductDialog);
